import React, { act, useEffect, useState } from 'react';
import './FilterListElement.css';
import { typeTagNames, attributeTagNames } from '../../../models/tag_representations';

import FilterDropdown from './FilterListElement/FilterDropdown';

import TypeTag from "../tags/TypeTag";
import MedicTag from "../tags/MedicTag";
import ImpactAttributeTag from "../tags/ImpactAttributeTag";

const sortedTagNames = Object.fromEntries(
    Object.entries(typeTagNames).sort()
);

const dateOptions = {
    "all": "All Time",
    "day": "Past Day",
    "week": "Past Week",
    "month": "Past Month",
    "year": "Past Year",
    "five-years": "Past 5 Years",
    "custom": "Custom"
}


const readOptions = { 
    "read": "Read",
    "unread": "Unread",
    "both": "Read & Unread"
}

const finishedOptions = {
    "finished": "Finished",
    "unfinished": "Unfinished",
    "both": "Finished & Unfinished"
}

const FilterListElement = ({ medicsData, testMetadata, filterState, filterDropdownsVisible, dictUpdateHandler, stateUpdateHandler, visible = true, showClearButton = true }) => {
    // Initialize medicsData and create medicsOptions
    medicsData = Array.isArray(medicsData) ? medicsData : [];
    const medicsOptions = {};
    for (const medic of medicsData) {
        if (medic["crew_member_id"] && medic["medic_name"]) {
            medicsOptions[medic["crew_member_id"]] = medic["medic_name"];
        }
    }
    const sortedMedicsOptions = new Map(
        Object.entries(medicsOptions).sort((a, b) => a[1].localeCompare(b[1]))
    );

    console.log('testMetadata:', testMetadata);

    // Initialize testMetadata and create measureOptions (similar to medicsOptions)
    testMetadata = Array.isArray(testMetadata) ? testMetadata : [];
    
    // Sort testMetadata by test_metadata_id
    const sortedTestMetadata = testMetadata.sort((a, b) => a.test_metadata_id - b.test_metadata_id);
    
    const measureOptions = {};
    for (const test of sortedTestMetadata) {
        if (test["test_metadata_id"] && test["test_metadata_id"] <= 100000 && test["test_metadata_id"] > 99 &&test["test_name"]) {
            measureOptions[test["test_name"]] = {
                name: test["test_name"],
                description: test["issue_message"],
                attribute: test["impact_attribute"] || "measure",
                type: test["test_type"] || "all"
            };
        }
    }
    console.log('measureOptions:', measureOptions);

    // Set initial filter state conditionally
    const [tagTypes, setTagTypes] = useState(filterState.tagTypes || []);
    const [medics, setMedics] = useState(filterState.medics || []);
    // Only default to "all" if filterState.date is explicitly undefined/null, not if it's an empty array
    const [date, setDate] = useState(filterState.date !== undefined && filterState.date !== null ? filterState.date : ["all"]);
    const [customStartDate, setCustomStartDate] = useState(filterState.customStartDate ?? "");
    const [customEndDate, setCustomEndDate] = useState(filterState.customEndDate ?? "");
    const [measure, setMeasure] = useState(filterState.measure || []);
    const [measureTrue, setMeasureTrue] = useState(filterState.measureTrue || []);
    const [read, setRead] = useState(filterState.read || ["both"]);
    const [finished, setFinished] = useState(filterState.finished || ["unfinished"]);
    const [hasFeedback, setHasFeedback] = useState(filterState.hasFeedback || false);
    const [showNarrativeSummary, setShowNarrativeSummary] = useState(filterState.showNarrativeSummary || false);
    const [incidentNumberSearch, setIncidentNumberSearch] = useState(filterState.incidentNumberSearch || "");

    // State for applied filters
    const [appliedTags, setAppliedTags] = useState([]);
    const [appliedMedics, setAppliedMedics] = useState([]);
    const [appliedMeasures, setAppliedMeasures] = useState([]);
    const [appliedMeasuresTrue, setAppliedMeasuresTrue] = useState([]);
    
    // Variable to control all dropdowns
    const [dropdown, setDropdown] = useState(null);

    const handleApplyFilters = () => {
        let newFilterDict = {};
        newFilterDict.readStatus = convertBooleans(read[0]);   
        newFilterDict.finishedStatus = convertBooleans(finished[0]);
        newFilterDict.reportType = tagTypes.join(",") || null; // || null added, perhaps not needed
        newFilterDict.crewMemberId = medics.join(",") || null;
        // Only set date range if a date is actually selected (not empty array)
        if (date && date.length > 0 && date[0]) {
            if (date[0] === "custom") {
                const startStr = customStartDate.trim();
                const endStr = customEndDate.trim();
                const startDate = parseMmDdYyyy(startStr);
                const endDate = parseMmDdYyyy(endStr);
                newFilterDict.lowerDate = startDate ? formatDateToString(startOfDay(startDate)) : null;
                newFilterDict.upperDate = endDate ? formatDateToString(endOfDay(endDate)) : null;
            } else {
                let dateRange = convertDate(date[0]); // date is in an array length 1
                newFilterDict.lowerDate = dateRange[0] ? formatDateToString(dateRange[0]) : null;
                newFilterDict.upperDate = dateRange[1] ? formatDateToString(dateRange[1]) : null;
            }
        } else {
            newFilterDict.lowerDate = null;
            newFilterDict.upperDate = null;
        }  
        // Handle measure selection - if it's an object, use the key, otherwise use the value directly
        const selectedMeasure = measure[0];
        newFilterDict.measureName = selectedMeasure || null;
        const selectedMeasureTrue = measureTrue[0];
        newFilterDict.measureTrueName = selectedMeasureTrue || null;
        newFilterDict.hasFeedback = hasFeedback ? 1 : null;
        newFilterDict.incidentNumberSearch = incidentNumberSearch || null;

        // console.log("newFilterDict", newFilterDict);
        
        dictUpdateHandler(newFilterDict);

        if (stateUpdateHandler) {
            stateUpdateHandler({tagTypes, medics, date, customStartDate, customEndDate, measure, measureTrue, read, finished, hasFeedback, showNarrativeSummary, incidentNumberSearch});
        }

        setAppliedTags(tagTypes);
        setAppliedMedics(medics);
        setAppliedMeasures(measure);
        setAppliedMeasuresTrue(measureTrue);

        // reset dropdowns
        setDropdown(!dropdown);
    }

    const handleClearFilters = () => {
        // Reset all filter states to default values
        setTagTypes([]);
        setMedics([]);
        setDate(["all"]);
        setCustomStartDate("");
        setCustomEndDate("");
        setMeasure([]);
        setMeasureTrue([]);
        setRead(["both"]);
        setFinished(["unfinished"]);
        setHasFeedback(false);
        setShowNarrativeSummary(false);
        setIncidentNumberSearch("");

        // Clear applied tags
        setAppliedTags([]);
        setAppliedMedics([]);
        setAppliedMeasures([]);
        setAppliedMeasuresTrue([]);

        // Create empty filter dictionary
        let newFilterDict = {};
        newFilterDict.readStatus = null;
        newFilterDict.finishedStatus = null;
        newFilterDict.reportType = null;
        newFilterDict.crewMemberId = null;
        newFilterDict.lowerDate = null;
        newFilterDict.upperDate = null;
        newFilterDict.measureName = null;
        newFilterDict.measureTrueName = null;
        newFilterDict.hasFeedback = null;
        newFilterDict.incidentNumberSearch = null;

        // Update handlers
        dictUpdateHandler(newFilterDict);

        if (stateUpdateHandler) {
            stateUpdateHandler({
                tagTypes: [], 
                medics: [], 
                date: ["all"], 
                customStartDate: "", 
                customEndDate: "", 
                measure: [], 
                measureTrue: [], 
                read: ["both"], 
                finished: ["unfinished"], 
                hasFeedback: false, 
                showNarrativeSummary: false, 
                incidentNumberSearch: ""
            });
        }

        // reset dropdowns
        setDropdown(!dropdown);
    }

    const convertBooleans = (value) => {
        //convert from string values (needed for dictionary keys) too booleans to return
        if (value === "both") {
            return null;
        } else if (value === "read" || value === "finished") {
            return true;
        } 
        return false;
    }

    const convertDate = (range) => {
        const curr = new Date();
        var dateRange = [new Date(0), curr];
        if (range === "all") {
            return dateRange;
        } else if (range === "day") {
            dateRange[0] = new Date(curr.getTime() - (24 * 60 * 60 * 1000)) // subtract 24hrs from today
        } else if (range === "week") {
            dateRange[0] = new Date(curr.getTime() - (7 * 24 * 60 * 60 * 1000)) // subtract 7 days from today
        } else if (range === "month") {
            dateRange[0] = new Date(curr);
            dateRange[0].setUTCMonth(curr.getUTCMonth() - 1);
            // Handle month overflow if necessary, ie Feb 31
            if (dateRange[0].getUTCMonth() === curr.getUTCMonth()) {
                dateRange[0].setUTCDate(0); // set to last day of previous month
            }
        } else if (range === "year") {
            dateRange[0] = new Date(curr);
            dateRange[0].setUTCFullYear(curr.getUTCFullYear() - 1);
        } else if (range === "five-years") {
            dateRange[0] = new Date(curr);
            dateRange[0].setUTCFullYear(curr.getUTCFullYear() - 5);
        }
        return dateRange;
    }

    function formatDateToString(date) {
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Months are zero-indexed
        const day = String(date.getUTCDate()).padStart(2, '0');
        const hours = String(date.getUTCHours()).padStart(2, '0');
        const minutes = String(date.getUTCMinutes()).padStart(2, '0');
        const seconds = String(date.getUTCSeconds()).padStart(2, '0');
    
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }

    /** Parse mm/dd/yyyy string to Date (UTC midnight for that calendar day). Returns null if invalid. */
    function parseMmDdYyyy(str) {
        if (!str || typeof str !== 'string') return null;
        const trimmed = str.trim();
        const match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (!match) return null;
        const [, m, d, y] = match;
        const month = parseInt(m, 10);
        const day = parseInt(d, 10);
        const year = parseInt(y, 10);
        if (month < 1 || month > 12 || day < 1 || day > 31) return null;
        const date = new Date(Date.UTC(year, month - 1, day));
        if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
        return date;
    }

    function startOfDay(d) {
        const out = new Date(d);
        out.setUTCHours(0, 0, 0, 0);
        return out;
    }

    function endOfDay(d) {
        const out = new Date(d);
        out.setUTCHours(23, 59, 59, 999);
        return out;
    }

    useEffect(() => {
        // Only auto-apply filters if a date is selected (not empty array)
        // This prevents auto-loading data when date filter is intentionally empty
        if (filterState && filterState.date && filterState.date.length > 0 && filterState.date[0]) {
            handleApplyFilters();
        }
    }, []);

    if (!visible) {
        return null;
    }

    return (
        <div className="list-element filter-list-element">
            <div className='row'>
                <div className='filters'>
                {filterDropdownsVisible?.typeTags && (
                    <FilterDropdown 
                        title="Type Tags" 
                        options={sortedTagNames} 
                        selected={tagTypes} 
                        updateSelected={setTagTypes} 
                        multiple={true}
                        dropdown={dropdown}
                    />
                )}
                {filterDropdownsVisible?.medics && (
                    <FilterDropdown 
                        title="Medics" 
                        options={sortedMedicsOptions} 
                        selected={medics} 
                        updateSelected={setMedics} 
                        multiple={true}
                        dropdown={dropdown}
                    />
                )}
                {filterDropdownsVisible?.date && (
                    <>
                        <FilterDropdown 
                            title="Date Range" 
                            options={dateOptions} 
                            selected={date}
                            updateSelected={setDate} 
                            multiple={false}
                            dropdown={dropdown}
                        />
                        {date[0] === "custom" && (
                            <div className="filter-custom-date">
                                <input
                                    type="text"
                                    placeholder="Start (mm/dd/yyyy)"
                                    value={customStartDate}
                                    onChange={(e) => setCustomStartDate(e.target.value)}
                                    className="filter-date-input"
                                    aria-label="Custom start date"
                                />
                                <input
                                    type="text"
                                    placeholder="End (mm/dd/yyyy)"
                                    value={customEndDate}
                                    onChange={(e) => setCustomEndDate(e.target.value)}
                                    className="filter-date-input"
                                    aria-label="Custom end date"
                                />
                            </div>
                        )}
                    </>
                )}
                {filterDropdownsVisible?.measures && (
                    <FilterDropdown 
                        title="Measures" 
                        options={measureOptions} 
                        selected={measure}
                        updateSelected={setMeasure}
                        multiple={false}
                        dropdown={dropdown}
                        renderOption={(optionKey, optionValue) => (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span>{optionValue.description}</span>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <ImpactAttributeTag attributeType={optionValue.attribute} isButton={false} />
                                    <TypeTag reportType={optionValue.type} isButton={false} />
                                    <div style={{ fontSize: '16px', marginTop: 'auto' }}>{optionValue.name}</div>
                                </div>
                            </div>
                        )}
                    />
                )}
                {filterDropdownsVisible?.measureTrue && (
                    <FilterDropdown 
                        title="Measure (result true)" 
                        options={measureOptions} 
                        selected={measureTrue}
                        updateSelected={setMeasureTrue}
                        multiple={false}
                        dropdown={dropdown}
                        renderOption={(optionKey, optionValue) => (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span>{optionValue.description}</span>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <ImpactAttributeTag attributeType={optionValue.attribute} isButton={false} />
                                    <TypeTag reportType={optionValue.type} isButton={false} />
                                    <div style={{ fontSize: '16px', marginTop: 'auto' }}>{optionValue.name}</div>
                                </div>
                            </div>
                        )}
                    />
                )}
                {filterDropdownsVisible?.read && (
                    <FilterDropdown 
                        title="Read" 
                        options={readOptions} 
                        selected={read}
                        updateSelected={setRead} 
                        multiple={false}
                        dropdown={dropdown}
                    />
                )}
                {filterDropdownsVisible?.finished && (
                    <FilterDropdown 
                        title="Finished" 
                        options={finishedOptions} 
                        selected={finished}
                        updateSelected={setFinished} 
                        multiple={false}
                        dropdown={dropdown}
                    />
                )}
                {filterDropdownsVisible?.hasFeedback && (
                    <div className="filter-checkbox" onClick={() => setHasFeedback(!hasFeedback)}>
                        <input 
                            type="checkbox" 
                            checked={hasFeedback}
                            onChange={(e) => setHasFeedback(e.target.checked)}
                            className="checkbox-input"
                        />
                        <span className="checkbox-text">Has Feedback</span>
                    </div>
                )}
                {filterDropdownsVisible?.showNarrativeSummary && (
                    <div className="filter-checkbox" onClick={() => {
                        const next = !showNarrativeSummary;
                        setShowNarrativeSummary(next);
                        if (stateUpdateHandler) {
                            stateUpdateHandler((prev) => ({ ...prev, showNarrativeSummary: next }));
                        }
                    }}>
                        <input 
                            type="checkbox" 
                            checked={showNarrativeSummary}
                            onChange={(e) => {
                                const next = e.target.checked;
                                setShowNarrativeSummary(next);
                                if (stateUpdateHandler) {
                                    stateUpdateHandler((prev) => ({ ...prev, showNarrativeSummary: next }));
                                }
                            }}
                            className="checkbox-input"
                        />
                        <span className="checkbox-text">Show narrative summary</span>
                    </div>
                )}
                {filterDropdownsVisible?.incidentNumberSearch && (
                    <div className="filter-search">
                        <input 
                            type="text" 
                            placeholder="Search Incident #"
                            value={incidentNumberSearch}
                            onChange={(e) => setIncidentNumberSearch(e.target.value)}
                            className="search-input"
                        />
                    </div>
                )}
                {showClearButton && (
                    <button className="clear-filters-btn" onClick={handleClearFilters}>
                        Clear Filters
                    </button>
                )}
            </div>
            <button className="submit" onClick={handleApplyFilters}>
                <i className="only-i fa-solid fa-filter"></i>
            </button>
        </div>
        
        {filterDropdownsVisible?.typeTags && appliedTags.length > 0 && (
            <div className='row'>
                <div className='applied-tags'>
                    <div className='tags'>
                    {appliedTags.map((tag) => (
                        <TypeTag key={tag} reportType={tag} isButton={false} />
                    ))}
                    </div>
                </div>
            </div>
        )}
        {filterDropdownsVisible?.medics && appliedMedics.length > 0 && (
            <div className='row'>
                <div className='applied-tags'>
                    <div className='tags'>
                        {appliedMedics.map((medic) => (
                            <MedicTag key={medic} medicNumber={sortedMedicsOptions.get(medic)} isButton={false}/>
                        ))}
                    </div>
                </div>
            </div>
        )}
        {filterDropdownsVisible?.measureTrue && appliedMeasuresTrue.length > 0 && (
            <div className='row'>
                <div className='applied-tags'>
                    <div className='tags'>
                        <span className='applied-measure-true-label'>Measure true: </span>
                        {appliedMeasuresTrue.map((m) => (
                            <span key={m} className='applied-measure-true'>{measureOptions[m]?.name ?? m}</span>
                        ))}
                    </div>
                </div>
            </div>
        )}
    </div>
    );
};

export default FilterListElement;